import { addDays, isSameDay } from '@helpers/DateHelper';

interface ICustodyData {
  morningWithMe: boolean | undefined;
  eveningWithMe: boolean | undefined;
  showName: boolean;
}

const mod = (n: number, m: number) => ((n % m) + m) % m;

// DST-safe “day key” + helpers (compare dates by calendar day, not time)
const dayKey = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
const diffDays = (a: Date, b: Date) => Math.floor((dayKey(a) - dayKey(b)) / 86400000);
const isBeforeDay = (a: Date, b: Date) => dayKey(a) < dayKey(b);
const isAfterDay = (a: Date, b: Date) => dayKey(a) > dayKey(b);

const startOfWeek = (d: Date, weekStartsOn: 0 | 1) => {
  let s = d;
  while (s.getDay() !== weekStartsOn) s = addDays(s, -1);
  return s;
};

const weeksNeeded = (year: number, month: number, localization: 'en' | 'se' ) => {
  const weekStartsOn: 0 | 1 = localization === 'en' ? 0 : 1; // US: Sun, SE: Mon
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const daysFromPrevMonth = mod(first.getDay() - weekStartsOn, 7);
  return daysFromPrevMonth + daysInMonth > 34 ? 6 : 5;
};

const adjustTemplate = (schedule: boolean[], offset: number, invert: boolean) => {
  const newSchedule: boolean[] = [];
  for (let i = 0; i < schedule.length; i += 1) {
    const srcIndex = mod(i - offset, schedule.length);
    const v = schedule[srcIndex];
    newSchedule[i] = invert ? !v : v;
  }
  return newSchedule;
};

/**
 * schedule[] is a repeating pattern where schedule[0] is ALWAYS Monday.
 * localization controls calendar week start:
 *   - 'us' => Sunday-first
 *   - 'se' => Monday-first
 */
const generateAdjustScheule = (
  displayBegin: Date,
  scheduleStart: Date,
  displayEnd: Date,
  scheduleEnd: Date,
  schedule: boolean[],
  localization: 'en' | 'se'
) => {
  if (scheduleEnd.getTime() <= scheduleStart.getTime()) return [];

  const weekStartsOn: 0 | 1 = localization === 'en' ? 0 : 1;

  // Anchor at the calendar's week-start containing scheduleStart
  const weekAnchor = startOfWeek(scheduleStart, weekStartsOn);

  // How many days from week-start to Monday? (US: 1, SE: 0)
  const daysToMonday = mod(1 - weekStartsOn, 7);

  const custody: ICustodyData[] = [];
  let lastNameWasMe: boolean | undefined;

  for (let d = displayBegin; !isAfterDay(d, displayEnd); d = addDays(d, 1)) {
    // Outside the schedule range => blank cell
    if (isBeforeDay(d, scheduleStart) || isAfterDay(d, scheduleEnd)) {
      custody.push({
        morningWithMe: undefined,
        eveningWithMe: undefined,
        showName: false,
      });
      continue;
    }

    // Index into a Monday-based schedule[] while the UI week may start Sunday
    const deltaFromWeekAnchor = diffDays(d, weekAnchor);
    const scheduleIndex = deltaFromWeekAnchor - daysToMonday;

    const morning =
      isSameDay(d, scheduleStart) ? undefined : schedule[mod(scheduleIndex - 1, schedule.length)];

    const evening =
      isSameDay(d, scheduleEnd) ? undefined : schedule[mod(scheduleIndex, schedule.length)];

    const entry: ICustodyData = {
      morningWithMe: morning,
      eveningWithMe: evening,
      showName: false,
    };

    if (evening !== undefined && morning === evening && lastNameWasMe !== evening) {
      entry.showName = true;
      lastNameWasMe = evening;
    }

    custody.push(entry);
  }

  return custody;
};

export { adjustTemplate, generateAdjustScheule, ICustodyData, weeksNeeded };



// WEBPACK FOOTER //
// ./src/components/guide/components/CalendarCalc.ts